import classNames from "classnames";
import { ActiveEntityType, RefundItem } from "../../build/activeEntityType";
import { NodeStates } from "../../build/types";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import { cloneNode } from "../../utils/cloneNode";
import { renderRepeaterDiv } from "../../utils/renderRepeaterDiv";
import { OrderDataComponentProps } from "../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import { DynamicDataSectionProvider } from "../../Contexts/DynamicDataTypeContext";

const BuilderOrderRefunds = ({
    node,
    builderProps,
    formatCurrency,
    order,
    children,
    disabled,
    layer
}: OrderDataComponentProps) => {
    const formatC = formatCurrency || ((amount?: number) => amount?.toString() || "");
    const { id, stateManager, attr } = node;
    const { setElement, states } = useInteractionStates<HTMLDivElement>();

    const orderDataClass = classNames("order-data-wrapper", {
        [`${attr?.className}`]: !!attr?.className
    });

    if (!children || !children.length) {
        ////OrderRefunds assumes receiving an "orderData" component, to which is passes every refund as a refundObj
        return <div>Component error refunds</div>;
    }

    if (builderProps) {
        const previewRefunds = order?.refund?.length
            ? order?.refund
            : [
                  {
                      ...order,
                      timestamp: "1",
                      refundPayment: order?.paymentMethods,
                      refundedBy: "Final POS",
                      tips: [],
                      refundReason: "Test",
                      refundNote: "Test"
                  } as RefundItem
              ];

        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        return (
            <div
                style={getCompoundStyle({
                    node,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
                className={orderDataClass}
            >
                {previewRefunds.map((refund, idx) => {
                    return (
                        <div
                            className={idx === 0 ? "single-refund" : "repeated-div-no-select"}
                            key={refund.timestamp}
                            style={idx === 0 ? {} : { opacity: 0.7, pointerEvents: "none" }}
                        >
                            {cloneNode({
                                node: children[0] as any,
                                index: idx,
                                keepFunctional: idx === 0,
                                dataObj: refund
                            })}
                        </div>
                    );
                })}
            </div>
        );
    } else {
        const wrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <div className={orderDataClass} style={wrapperStyle} ref={setElement}>
                {order?.refund?.length
                    ? order.refund.map((refund, idx) => {
                          const newRefund = {
                              ...refund,
                              summary: { ...refund?.summary },
                              paymentMethods: refund.refundPayment
                                  .map(payment => {
                                      return `
                                ${payment.paymentType}: ${formatC(+payment.amount)}
                            `;
                                  })
                                  .join(",")
                          } as any;
                          newRefund.paymentMethods = `
                            ${refund?.refundPayment
                                ?.map(payment => {
                                    return `
                                    ${payment.paymentType}: ${formatC(+payment.amount)}
                                `;
                                })
                                .join(",")}
                        `;

                          if (!newRefund.lineItems || !layer) return null;

                          return (
                              <DynamicDataSectionProvider
                                  value={{ dynamicDataScope: ActiveEntityType.ORDER, dynamicDataObject: newRefund }}
                              >
                                  <div className="single-refund" key={`${refund.timestamp}-${idx}`}>
                                      {renderRepeaterDiv({
                                          baseLayer: layer,
                                          nodeKey: (children[0] as any)!.props.node.id,
                                          dataObj: newRefund,
                                          defaultCallback: () => {},
                                          index: idx,
                                          extraProps: { refundObj: newRefund, layer: layer },
                                          formatCurrency: formatC
                                      })}
                                  </div>
                              </DynamicDataSectionProvider>
                          );
                      })
                    : null}
            </div>
        );
    }
};

export default BuilderOrderRefunds;
